import { useEffect, useState, type ChangeEvent, type FormEvent } from 'react'
import { isAxiosError } from 'axios'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import {
    generateImagesApiV1GenerationsOrgIdGenerateImagesProductProductIdPost,
    getGenerationGroupDetailApiV1GenerationsOrgIdGenerationImageGroupsGenerationGroupIdGet,
    getImageGenerationGroupsApiV1GenerationsOrgIdGenerationImageGroupsProductProductIdGet,
    modifyImagesApiV1GenerationsOrgIdModifyImagesProductProductIdPost,
    regenerateImagesApiV1GenerationsOrgIdRegenerateImagesProductProductIdPost,
} from '@/common/api/client/sdk.gen'
import type {
    BodyGenerateImagesApiV1GenerationsOrgIdGenerateImagesProductProductIdPost,
    GenerateGroupImagesDetailResponse,
    GenerateGroupItemImageDetailResponse,
    GenerationStatus,
    GenerationGroupCreateResponse,
    ImageModifyParams,
    ImageRegenerateParams,
    PageGenerateGroupImagesDetailResponse,
} from '@/common/api/client/types.gen'
import { detailQueryKey, useProductFlow, type ProductFlowState } from './productFlow'
import {
    isInsufficientBalanceError,
    openInsufficientBalanceModal,
} from '../../insufficientBalance/model/insufficientBalanceModal'

const MEDIA_IMAGE_GROUPS_FLOW_KEY = 'media-image-groups'
const GENERATION_POLLING_STATUSES: GenerationStatus[] = ['created', 'pending', 'running']

type ProductMediaImageModifyErrorResponse = {
    detail?: string
}

type ProductMediaImageModifyFormValues = {
    image_user_prompt: string
    text_user_prompt: string
}

type ProductMediaImageModifyMode = 'text' | 'image' | 'regenerate'

const getProductMediaImageModifyErrorMessage = (error: unknown, fallback: string) => {
    if (isAxiosError<ProductMediaImageModifyErrorResponse>(error)) {
        return error.response?.data?.detail ?? fallback
    }

    if (error instanceof Error && error.message) {
        return error.message
    }

    return fallback
}

export const useProductMediaImageGroupsFlow = (): ProductFlowState<
    PageGenerateGroupImagesDetailResponse,
    GenerateGroupImagesDetailResponse,
    BodyGenerateImagesApiV1GenerationsOrgIdGenerateImagesProductProductIdPost,
    GenerationGroupCreateResponse
> => {
    return useProductFlow<
        PageGenerateGroupImagesDetailResponse,
        GenerateGroupImagesDetailResponse,
        BodyGenerateImagesApiV1GenerationsOrgIdGenerateImagesProductProductIdPost,
        GenerationGroupCreateResponse
    >({
        flowKey: MEDIA_IMAGE_GROUPS_FLOW_KEY,
        fetchList: async ({ orgId, productId, page, pageSize }) => {
            const res = await getImageGenerationGroupsApiV1GenerationsOrgIdGenerationImageGroupsProductProductIdGet({
                path: { org_id: orgId, product_id: productId },
                query: {
                    order_by: 'created_at',
                    direction: 'desc',
                    page,
                    size: pageSize,
                },
            })
            return res.data!
        },
        selectIdFromList: list => list?.items[0]?.id ?? null,
        extractIdFromMutationResult: r => r.group_id,
        fetchDetail: async (orgId, groupId) => {
            const res = await getGenerationGroupDetailApiV1GenerationsOrgIdGenerationImageGroupsGenerationGroupIdGet({
                path: { org_id: orgId, generation_group_id: groupId },
            })
            return res.data!
        },
        runMutation: async ({ orgId, productId, variables }) => {
            const res = await generateImagesApiV1GenerationsOrgIdGenerateImagesProductProductIdPost({
                path: { org_id: orgId, product_id: productId },
                body: variables,
            })
            return res.data!
        },
    })
}

export type ProductMediaImageGroupsState = ProductFlowState<
    PageGenerateGroupImagesDetailResponse,
    GenerateGroupImagesDetailResponse,
    BodyGenerateImagesApiV1GenerationsOrgIdGenerateImagesProductProductIdPost,
    GenerationGroupCreateResponse
>

export type ProductMediaImageModifyVariables = {
    generation: GenerateGroupItemImageDetailResponse
    nextUserPrompt?: string
    mode: ProductMediaImageModifyMode
}

type UseProductMediaImageModifyMutationParams = {
    orgId: string | undefined
    productId: string | undefined
    flowKey: string
    groupId: string
    onSubmittedGenerationId?: (generationId: string | null) => void
    onError?: (error: unknown, variables: ProductMediaImageModifyVariables) => void
}

export const useProductMediaImageModifyMutation = ({
    orgId,
    productId,
    flowKey,
    groupId,
    onSubmittedGenerationId,
    onError,
}: UseProductMediaImageModifyMutationParams) => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async ({ generation, nextUserPrompt, mode }: ProductMediaImageModifyVariables) => {
            if (!orgId) {
                throw new Error('Организация не выбрана')
            }
            if (!productId) {
                throw new Error('Товар не найден')
            }

            if (mode === 'regenerate') {
                const response = await regenerateImagesApiV1GenerationsOrgIdRegenerateImagesProductProductIdPost({
                    path: {
                        org_id: orgId,
                        product_id: productId,
                    },
                    body: {
                        generation_id: generation.id,
                    } satisfies ImageRegenerateParams,
                })

                return response.data
            }

            const assetId = generation.asset_id
            const prompt = nextUserPrompt

            if (!assetId) {
                throw new Error('Не найден ассет изображения')
            }
            if (!prompt) {
                throw new Error('Промпт не заполнен')
            }

            const body = {
                generation_group_id: groupId,
                generation_id: generation.id,
                modify_text: mode === 'text',
                image: assetId,
                user_prompt: prompt,
            } satisfies ImageModifyParams

            const response = await modifyImagesApiV1GenerationsOrgIdModifyImagesProductProductIdPost({
                path: {
                    org_id: orgId,
                    product_id: productId,
                },
                body,
            })

            return response.data
        },
        onSuccess: data => {
            if (!orgId || !productId) {
                return
            }

            const nextGenerationId = data?.generations?.[0]?.generation_id ?? null
            onSubmittedGenerationId?.(nextGenerationId)
            void queryClient.invalidateQueries({
                queryKey: ['product-flow', flowKey, 'list', orgId, productId],
            })
            void queryClient.invalidateQueries({
                queryKey: detailQueryKey(flowKey, orgId, groupId),
            })
            if (data?.group_id) {
                void queryClient.invalidateQueries({
                    queryKey: detailQueryKey(flowKey, orgId, data.group_id),
                })
            }
        },
        onError: (error, variables) => {
            if (isInsufficientBalanceError(error)) {
                openInsufficientBalanceModal()
            }

            onError?.(error, variables)
        },
    })
}

type UseProductMediaImageModifyFormParams = {
    orgId: string | undefined
    productId: string | undefined
    flowKey: string
    groupId: string
    selectedGeneration: GenerateGroupItemImageDetailResponse | undefined
    detailGenerations: GenerateGroupItemImageDetailResponse[] | undefined
    onSubmittedGenerationId: (generationId: string | null) => void
}

export const useProductMediaImageModifyForm = ({
    orgId,
    productId,
    flowKey,
    groupId,
    selectedGeneration,
    detailGenerations,
    onSubmittedGenerationId,
}: UseProductMediaImageModifyFormParams) => {
    const [activeModifyMode, setActiveModifyMode] = useState<Exclude<ProductMediaImageModifyMode, 'regenerate'>>('text')
    const form = useForm<ProductMediaImageModifyFormValues>({
        defaultValues: {
            image_user_prompt: '',
            text_user_prompt: '',
        },
    })
    const imageUserPrompt = form.watch('image_user_prompt')
    const textUserPrompt = form.watch('text_user_prompt')

    useEffect(() => {
        form.reset({
            image_user_prompt: selectedGeneration?.user_prompt ?? '',
            text_user_prompt: selectedGeneration?.text_on_image ?? '',
        })
    }, [form, selectedGeneration?.id, selectedGeneration?.text_on_image, selectedGeneration?.user_prompt])

    const modifyImageMutation = useProductMediaImageModifyMutation({
        orgId,
        productId,
        flowKey,
        groupId,
        onSubmittedGenerationId,
        onError: (error, variables) => {
            const fallback =
                variables.mode === 'text'
                    ? 'Не удалось отправить изменение текста'
                    : variables.mode === 'image'
                      ? 'Не удалось отправить изменение картинки'
                      : 'Не удалось выполнить перегенерацию'
            const message = getProductMediaImageModifyErrorMessage(error, fallback)

            form.setError('root', {
                type: 'server',
                message,
            })
            if (variables.mode === 'text') {
                form.setError('text_user_prompt', {
                    type: 'server',
                    message,
                })
            }
            if (variables.mode === 'image') {
                form.setError('image_user_prompt', {
                    type: 'server',
                    message,
                })
            }
        },
    })

    const handleModifyModeChange = (index: number) => {
        setActiveModifyMode(index === 1 ? 'image' : 'text')
        form.clearErrors('root')
    }

    const handleTextUserPromptChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
        form.setValue('text_user_prompt', event.target.value, { shouldDirty: true })
        form.clearErrors('root')
        form.clearErrors('text_user_prompt')
    }

    const handleImageUserPromptChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
        form.setValue('image_user_prompt', event.target.value, { shouldDirty: true })
        form.clearErrors('root')
        form.clearErrors('image_user_prompt')
    }

    const handleModifyTextSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        if (!selectedGeneration) {
            return
        }

        const nextUserPrompt = textUserPrompt.trim()
        if (!nextUserPrompt) {
            form.setError('text_user_prompt', { type: 'required', message: 'Введите промпт' })
            return
        }

        form.clearErrors('root')
        form.clearErrors('text_user_prompt')
        modifyImageMutation.mutate({ generation: selectedGeneration, nextUserPrompt, mode: 'text' })
    }

    const handleModifyImageSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        if (!selectedGeneration) {
            return
        }

        const nextUserPrompt = imageUserPrompt.trim()
        if (!nextUserPrompt) {
            form.setError('image_user_prompt', { type: 'required', message: 'Введите промпт' })
            return
        }

        form.clearErrors('root')
        form.clearErrors('image_user_prompt')
        modifyImageMutation.mutate({ generation: selectedGeneration, nextUserPrompt, mode: 'image' })
    }

    const handleRegenerateSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        if (!selectedGeneration) {
            return
        }

        form.clearErrors('root')
        modifyImageMutation.mutate({ generation: selectedGeneration, mode: 'regenerate' })
    }

    const activeModifyVariables = modifyImageMutation.variables
    const isSelectedModifyTextMutation =
        activeModifyVariables?.generation.id === selectedGeneration?.id && activeModifyVariables?.mode === 'text'
    const isSelectedModifyImageMutation =
        activeModifyVariables?.generation.id === selectedGeneration?.id && activeModifyVariables?.mode === 'image'
    const isSelectedRegenerateMutation =
        activeModifyVariables?.generation.id === selectedGeneration?.id && activeModifyVariables?.mode === 'regenerate'
    const isSelectedModifyTextPending = modifyImageMutation.isPending && isSelectedModifyTextMutation
    const isSelectedModifyImagePending = modifyImageMutation.isPending && isSelectedModifyImageMutation
    const isSelectedRegeneratePending = modifyImageMutation.isPending && isSelectedRegenerateMutation
    const isSelectedModifyTextError = modifyImageMutation.isError && isSelectedModifyTextMutation
    const isSelectedModifyImageError = modifyImageMutation.isError && isSelectedModifyImageMutation
    const isSelectedRegenerateError = modifyImageMutation.isError && isSelectedRegenerateMutation
    const textUserPromptErrorMessage = form.formState.errors.text_user_prompt?.message
    const imageUserPromptErrorMessage = form.formState.errors.image_user_prompt?.message
    const rootErrorMessage = form.formState.errors.root?.message
    const selectedModifyTextErrorMessage =
        textUserPromptErrorMessage ?? (isSelectedModifyTextError ? rootErrorMessage : undefined)
    const selectedModifyImageErrorMessage =
        imageUserPromptErrorMessage ?? (isSelectedModifyImageError ? rootErrorMessage : undefined)
    const selectedRegenerateErrorMessage = isSelectedRegenerateError ? rootErrorMessage : undefined
    const isDetailGenerationPolling = Boolean(
        detailGenerations?.some(generation => GENERATION_POLLING_STATUSES.includes(generation.status))
    )
    const canSubmitModifyText =
        Boolean(selectedGeneration?.asset_id) && !isDetailGenerationPolling && !isSelectedModifyTextPending
    const canSubmitModifyImage =
        Boolean(selectedGeneration?.asset_id) && !isDetailGenerationPolling && !isSelectedModifyImagePending
    const canSubmitRegenerate = Boolean(selectedGeneration) && !isSelectedRegeneratePending

    return {
        activeModifyMode,
        form,
        imageUserPrompt,
        textUserPrompt,
        handleImageUserPromptChange,
        handleModifyImageSubmit,
        handleModifyModeChange,
        handleModifyTextSubmit,
        handleTextUserPromptChange,
        handleRegenerateSubmit,
        isSelectedModifyImagePending,
        isSelectedModifyTextPending,
        isSelectedRegeneratePending,
        isSelectedModifyImageError,
        isSelectedModifyTextError,
        isSelectedRegenerateError,
        selectedModifyImageErrorMessage,
        selectedModifyTextErrorMessage,
        selectedRegenerateErrorMessage,
        canSubmitModifyImage,
        canSubmitModifyText,
        canSubmitRegenerate,
    }
}
